const PLACEHOLDER_PATTERN = /\{\{([^}]+)\}\}/g;
const NAMED_PLACEHOLDER_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const POSITIONAL_PLACEHOLDER_PATTERN = /^\d+$/;

export const TEMPLATE_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
export const TEMPLATE_PARAMETER_FORMATS = ['POSITIONAL', 'NAMED'];
export const TEMPLATE_HEADER_FORMATS = [
  'TEXT',
  'IMAGE',
  'VIDEO',
  'DOCUMENT',
  'LOCATION',
];
export const TEMPLATE_BUTTON_TYPES = [
  'URL',
  'PHONE_NUMBER',
  'QUICK_REPLY',
  'COPY_CODE',
];
export const TEMPLATE_LANGUAGES = [
  'en_US',
  'en',
  'pt_BR',
  'pt_PT',
  'es',
  'es_ES',
  'fr',
  'de',
  'it',
  'nl',
  'ru',
  'ar',
  'hi',
  'id',
  'ja',
  'ko',
  'zh_CN',
];

export const createTemplateFormState = (inboxes, initialInboxId = null) => ({
  inboxId:
    inboxes.find(inbox => String(inbox.id) === String(initialInboxId))?.id ||
    inboxes[0]?.id ||
    null,
  name: '',
  language: '',
  category: 'UTILITY',
  parameterFormat: 'POSITIONAL',
  body: '',
  bodyExamples: '',
  headerEnabled: false,
  headerFormat: 'TEXT',
  headerText: '',
  headerHandle: '',
  headerExample: '',
  headerLatitude: '',
  headerLongitude: '',
  footer: '',
  buttons: [],
  namedParameters: [],
});

export const parseBodyExamples = value =>
  (value || '')
    .split(/[\n,]/)
    .map(example => example.trim())
    .filter(Boolean);

export const extractPlaceholders = (...values) => {
  const placeholders = values
    .filter(Boolean)
    .flatMap(value => [...String(value).matchAll(PLACEHOLDER_PATTERN)])
    .map(match => match[1].trim())
    .filter(Boolean);

  return [...new Set(placeholders)];
};

export const extractNamedParameters = (...values) =>
  extractPlaceholders(...values).filter(parameter =>
    NAMED_PLACEHOLDER_PATTERN.test(parameter)
  );

export const extractPositionalParameters = (...values) =>
  extractPlaceholders(...values).filter(parameter =>
    POSITIONAL_PLACEHOLDER_PATTERN.test(parameter)
  );

export const getNamedParameterMap = namedParameters =>
  Object.fromEntries(
    namedParameters
      .filter(parameter => parameter.name && parameter.value)
      .map(parameter => [parameter.name, parameter.value])
  );

export const isButtonValid = button => {
  if (!button?.text?.trim()) return false;
  if (button.type === 'URL') {
    if (!button.url?.trim()) return false;
    return !/\{\{[^}]+\}\}/.test(button.url) || Boolean(button.example?.trim());
  }
  if (button.type === 'PHONE_NUMBER') {
    return Boolean(button.phone_number?.trim());
  }
  if (button.type === 'COPY_CODE') return Boolean(button.example?.trim());
  return true;
};

const buildNamedExample = (parameterNames, namedParameters) => {
  const values = getNamedParameterMap(namedParameters);
  const params = parameterNames
    .filter(name => values[name])
    .map(name => ({ param_name: name, example: values[name] }));

  return params.length ? { body_text_named_params: params } : undefined;
};

const buildHeader = state => {
  if (!state.headerEnabled) return undefined;

  const header = { format: state.headerFormat };
  if (state.headerFormat === 'TEXT') {
    header.text = state.headerText;
    const namedHeaderParameters = extractNamedParameters(state.headerText);
    const namedExample = buildNamedExample(
      namedHeaderParameters,
      state.namedParameters
    );
    if (namedExample) {
      header.example = {
        header_text_named_params: namedExample.body_text_named_params,
      };
    } else if (state.headerExample) {
      header.example = { header_text: [state.headerExample] };
    }
  } else if (state.headerFormat === 'LOCATION') {
    header.example = {
      latitude: Number(state.headerLatitude),
      longitude: Number(state.headerLongitude),
    };
  } else if (state.headerHandle) {
    header.example = { header_handle: [state.headerHandle] };
  }

  return header;
};

const buildButtons = buttons =>
  buttons.map(button => {
    const result = {
      type: button.type,
      text: button.text.trim(),
    };

    if (button.type === 'URL') {
      result.url = button.url.trim();
      if (button.example) result.example = [button.example.trim()];
    } else if (button.type === 'PHONE_NUMBER') {
      result.phone_number = button.phone_number.trim();
    } else if (button.type === 'COPY_CODE' && button.example) {
      result.example = button.example.trim();
    }

    return result;
  });

export const buildTemplatePayload = state => {
  const payload = {
    name: state.name.trim(),
    language: state.language,
    category: state.category,
    parameter_format: state.parameterFormat,
    body: state.body.trim(),
  };

  const bodyExamples = parseBodyExamples(state.bodyExamples);
  if (state.parameterFormat === 'POSITIONAL' && bodyExamples.length) {
    payload.body_examples = bodyExamples;
  }

  const bodyNamedParameters = extractNamedParameters(state.body);
  const namedExample = buildNamedExample(
    bodyNamedParameters,
    state.namedParameters
  );
  if (namedExample) payload.body_example = namedExample;

  const header = buildHeader(state);
  if (header) payload.header = header;
  if (state.footer.trim()) payload.footer = state.footer.trim();
  if (state.buttons.length) payload.buttons = buildButtons(state.buttons);

  return payload;
};

export const buildPreviewVariables = state => {
  if (state.parameterFormat === 'NAMED') {
    return getNamedParameterMap(state.namedParameters);
  }

  const examples = parseBodyExamples(state.bodyExamples);
  const bodyVariables = Object.fromEntries(
    extractPositionalParameters(state.body).map((parameter, index) => [
      parameter,
      examples[index] || `[${parameter}]`,
    ])
  );
  const headerExamples = state.headerExample ? [state.headerExample] : examples;
  const headerVariables = Object.fromEntries(
    extractPositionalParameters(state.headerText).map((parameter, index) => [
      parameter,
      headerExamples[index] || `[${parameter}]`,
    ])
  );

  return { ...bodyVariables, ...headerVariables };
};

export const buildPreviewTemplate = (state, previewCopy = {}) => {
  const components = [];
  if (state.headerEnabled && state.headerFormat === 'TEXT') {
    components.push({
      type: 'HEADER',
      format: 'TEXT',
      text: state.headerText || previewCopy.header,
    });
  }

  components.push({
    type: 'BODY',
    text: state.body || previewCopy.body,
  });

  if (state.footer) {
    components.push({ type: 'FOOTER', text: state.footer });
  }

  if (state.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: state.buttons.map(button => ({
        type: button.type,
        text: button.text || previewCopy.button,
      })),
    });
  }

  return {
    name: state.name || previewCopy.name,
    language: state.language || 'en_US',
    category: state.category || 'UTILITY',
    parameter_format: state.parameterFormat,
    components,
  };
};
